/**
 * use-billing-invoice-settings.ts
 *
 * Global billing settings hook — Invoice Numbering (Feature 9).
 *
 * Fetches from GET /api/company/billing-settings on mount and persists
 * changes via PATCH /api/company/billing-settings on save.
 * Falls back to localStorage in demo mode.
 */

import { useState, useCallback, useEffect } from 'react';
import { getCompanyBillingSettings, patchCompanyBillingSettings, getDataMode } from '../lib/data-service';

// ── Types ────────────────────────────────────────────────────────────────────

export interface BillingInvoiceSettings {
  /** When true, invoice numbers are assigned automatically by Access Financials. */
  autoNumberingEnabled: boolean;
  /**
   * The last invoice number used in the firm's sequence.
   * Only relevant when autoNumberingEnabled is false — shown as a helper hint.
   */
  lastInvoiceNumber: string;
}

export interface UseBillingInvoiceSettingsReturn {
  settings: BillingInvoiceSettings;
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  isLoading: boolean;
  updateAutoNumbering: (enabled: boolean) => void;
  save: () => Promise<void>;
  discard: () => void;
}

// ── Module-level cache for standalone reader ─────────────────────────────────

let _cachedSettings: BillingInvoiceSettings = {
  autoNumberingEnabled: true,
  lastInvoiceNumber: '',
};

// ── Demo fallback ────────────────────────────────────────────────────────────

const DEMO_DEFAULTS: BillingInvoiceSettings = {
  autoNumberingEnabled: true,
  lastInvoiceNumber: 'INV-2025-004',
};

const DEMO_STORAGE_KEY = 'evo_billing_invoice_settings_v1';

function loadDemoSettings(): BillingInvoiceSettings {
  try {
    const raw = localStorage.getItem(DEMO_STORAGE_KEY);
    if (!raw) return { ...DEMO_DEFAULTS };
    const parsed = JSON.parse(raw) as { autoNumberingEnabled?: boolean };
    return {
      autoNumberingEnabled: parsed.autoNumberingEnabled ?? DEMO_DEFAULTS.autoNumberingEnabled,
      lastInvoiceNumber: DEMO_DEFAULTS.lastInvoiceNumber,
    };
  } catch {
    return { ...DEMO_DEFAULTS };
  }
}

function saveDemoSettings(settings: { autoNumberingEnabled: boolean }): void {
  try {
    localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify({ autoNumberingEnabled: settings.autoNumberingEnabled }));
  } catch {
    // Ignore storage errors in demo
  }
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useBillingInvoiceSettings(): UseBillingInvoiceSettingsReturn {
  const [saved, setSaved] = useState<BillingInvoiceSettings>(_cachedSettings);
  const [current, setCurrent] = useState<BillingInvoiceSettings>(_cachedSettings);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const mode = getDataMode();

    if (mode === 'demo') {
      const demoSettings = loadDemoSettings();
      _cachedSettings = demoSettings;
      setSaved(demoSettings);
      setCurrent(demoSettings);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    getCompanyBillingSettings()
      .then((response) => {
        if (cancelled) return;
        const settings: BillingInvoiceSettings = {
          autoNumberingEnabled: response.autoNumberingEnabled,
          lastInvoiceNumber: response.lastInvoiceNumber ?? '',
        };
        _cachedSettings = settings;
        setSaved(settings);
        setCurrent(settings);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[BillingSettings] Failed to fetch billing settings, using defaults', err);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  const hasUnsavedChanges = current.autoNumberingEnabled !== saved.autoNumberingEnabled;

  const updateAutoNumbering = useCallback((enabled: boolean) => {
    setCurrent((prev) => ({ ...prev, autoNumberingEnabled: enabled }));
  }, []);

  const save = useCallback(async () => {
    setIsSaving(true);
    try {
      const mode = getDataMode();
      if (mode === 'demo') {
        await new Promise((resolve) => setTimeout(resolve, 400));
        saveDemoSettings({ autoNumberingEnabled: current.autoNumberingEnabled });
      } else {
        const response = await patchCompanyBillingSettings({ autoNumberingEnabled: current.autoNumberingEnabled });
        current.lastInvoiceNumber = response.lastInvoiceNumber ?? current.lastInvoiceNumber;
      }
      _cachedSettings = { ...current };
      setSaved({ ...current });
    } finally {
      setIsSaving(false);
    }
  }, [current]);

  const discard = useCallback(() => {
    setCurrent(saved);
  }, [saved]);

  return {
    settings: current,
    hasUnsavedChanges,
    isSaving,
    isLoading,
    updateAutoNumbering,
    save,
    discard,
  };
}

// ── Standalone reader (for use outside React components) ─────────────────────

/**
 * Returns the current billing invoice settings from the module-level cache.
 * The cache is populated by the useBillingInvoiceSettings hook on mount.
 * Falls back to defaults if the hook has not yet fetched the settings.
 */
export function getBillingInvoiceSettings(): BillingInvoiceSettings {
  return { ..._cachedSettings };
}
